import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormGroup } from "react-bootstrap";
import { CustomFileUpload } from "../CustomFileUpload";
import { CustomSwitch } from "../CustomSwitch";
import {
  getAttachmentEndpoint,
  removeAttachment,
  uploadAttachment,
} from "../../../api/clinicalTrial";
import { useFiles } from "../Files/Hooks";
import { Spinner } from "../../Utils/Loaders";
import { DocumentViewer } from "../DocViewer/DocumentViewer";
import defaultExtendedMetadata from "./defaultExtendedMetadata";
import { CustomSelect } from "../CustomSelect";
import FileHistoryModal from "./FileHistory";
import { FaHistory } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { ModalConfirm } from "../../Modals/ModalConfirm";
import { CustomDate } from "../CustomDateTime";
import { decodeFileName } from "../../Utils/Commons";
import { attachmentCategories } from "../../../variables/Enums";

const DocumentUploadCard = ({
  attachmentEntityTypeId,
  entityId,
  attachment,
  legend,
  onAttachmentChange,
  onCategoryNameChange,
  showRestoreButton = true,
  disabled = false,
  showLegend = false,
  editCategoryName = false,
}) => {
  const { t } = useTranslation();
  const [attachmentMetadata, setAttachmentMetadata] = useState();
  const [file, setFile] = useState();
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [categoryName, setCategoryName] = useState(
    attachment?.attachmentCategoryName || ""
  );
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setCategoryName(attachment?.attachmentCategoryName || "");
  }, [attachment?.attachmentCategoryName]);

  const [response, downloadBlob] = useFiles(
    getAttachmentEndpoint(attachmentEntityTypeId)
  );

  useEffect(() => {
    if (!attachment) return;

    const decodedFileName = decodeFileName(attachment.attachmentFileName);
    setFile({ fileName: decodedFileName });

    if (!!attachment.extendedMetadata) {
      try {
        const jsonDeserializedMetadata = JSON.parse(
          attachment.extendedMetadata
        );
        const metadata = {
          upperFields: jsonDeserializedMetadata.upperFields || [],
          lowerFields: jsonDeserializedMetadata.lowerFields || [],
          ...jsonDeserializedMetadata,
        };
        if (attachment.attachmentCategoryId === attachmentCategories.delegationFile) {
          metadata.upperFields = [];
        }
        setAttachmentMetadata(metadata);
      } catch (error) {
        if (attachment.attachmentCategoryId) {
          const defaultMetadata =
            defaultExtendedMetadata[attachment.attachmentCategoryId] || {};
          setAttachmentMetadata(defaultMetadata);
        }
      }
    } else {
      if (attachment.attachmentCategoryId) {
        const defaultMetadata =
          defaultExtendedMetadata[attachment.attachmentCategoryId] || {};
        setAttachmentMetadata(defaultMetadata);
      }
    }
  }, [attachment]);

  const handlePreviewFile = async () => {
    await downloadBlob(attachment.id);
  };

  const validateDateNotFuture = (fieldName, fieldValue, categoryId) => {
    if (
      fieldName === "communicationFileDate" &&
      (categoryId === 3 || categoryId === 32)
    ) {
      if (!fieldValue) {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
        return true;
      }

      let selectedDate;
      if (typeof fieldValue === "string") {
        selectedDate = new Date(fieldValue);
      } else if (fieldValue instanceof Date) {
        selectedDate = fieldValue;
      } else if (
        fieldValue &&
        typeof fieldValue === "object" &&
        fieldValue._isAMomentObject
      ) {
        selectedDate = fieldValue.toDate();
      } else if (
        fieldValue &&
        typeof fieldValue === "object" &&
        fieldValue.format
      ) {
        selectedDate = fieldValue.toDate();
      } else {
        return true;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateToCompare = new Date(selectedDate);
      dateToCompare.setHours(0, 0, 0, 0);

      if (dateToCompare > today) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldName]: "La fecha no puede ser futura",
        }));
        return false;
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
        return true;
      }
    } else {
      return true;
    }
  };

  const handleAttachmentMetadataChange = (
    fieldSection,
    fieldName,
    fieldValue
  ) => {
    const newAttachmentMetadata = { ...attachmentMetadata };
    const fieldToUpdate = newAttachmentMetadata[fieldSection].find(
      (f) => f.name === fieldName
    );

    let valueToSet = fieldValue;
    if (fieldName === "communicationFileDate") {
      const isValid = validateDateNotFuture(
        fieldName,
        fieldValue,
        attachment?.attachmentCategoryId
      );
      if (!isValid) {
        valueToSet = "";
      }
    }

    if (fieldToUpdate) {
      fieldToUpdate.value = valueToSet;
    }

    const newAttachment = { ...attachment };
    newAttachment.extendedMetadata = JSON.stringify(newAttachmentMetadata);
    onAttachmentChange(newAttachment);
  };

  const createField = (fieldSection, field, key) => {
    switch (field.type) {
      case "date": {
        const fieldError = fieldErrors[field.name];
        return (
          <Col key={key} md={field.fullWidth ? 12 : 6}>
            <FormGroup>
              <label htmlFor={field.name}>{field.label}</label>
              <CustomDate
                dateFormat="DD/MM/YYYY"
                timeFormat={true}
                inputProps={{ placeholder: "DD/MM/AAAA" }}
                value={field.value}
                name={field.name}
                disabled={disabled}
                onChange={(name, value) => {
                  handleAttachmentMetadataChange(fieldSection, name, value);
                }}
                viewMode="years"
                closeOnSelect={true}
              />
              {fieldError && (
                <label
                  style={{
                    color: "red",
                    fontSize: "12px",
                    display: "block",
                    marginTop: "5px",
                  }}
                >
                  {fieldError}
                </label>
              )}
            </FormGroup>
          </Col>
        );
      }

      case "switch":
        return (
          <Col key={key} md={field.fullWidth ? 12 : 6}>
            <FormGroup>
              <label htmlFor={field.name}>{field.label}</label>
              <div>
                <CustomSwitch
                  name={field.name}
                  value={field.value}
                  disabled={disabled}
                  onChange={(_, value) => {
                    handleAttachmentMetadataChange(
                      fieldSection,
                      field.name,
                      value
                    );
                  }}
                />
              </div>
            </FormGroup>
          </Col>
        );

      case "select": {
        const selectOptions = field.options.map((x) => ({
          value: x.value,
          label: t(x.label),
        }));

        const selectedOption = field.value
          ? selectOptions.find((opt) => opt.value === field.value)
          : null;

        return (
          <Col key={key} md={field.fullWidth ? 12 : 6}>
            <FormGroup>
              <label className="form-check-label mb-0" htmlFor={field.name}>
                {field.label}
              </label>
              <CustomSelect
                value={selectedOption}
                name={field.name}
                onChange={(name, selectedOption) => {
                  const valueToSave = selectedOption
                    ? selectedOption.value
                    : null;
                  handleAttachmentMetadataChange(
                    fieldSection,
                    field.name,
                    valueToSave
                  );
                }}
                disabled={disabled}
                isMulti={field.multi}
                isClearable={field.clearable}
                loading={false}
                options={selectOptions}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />
            </FormGroup>
          </Col>
        );
      }

      default:
        break;
    }
  };

  return (
    <>
      {!attachment && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner />
        </div>
      )}
      {!!attachment && (
        <div style={{ position: "relative" }}>
          {!!editCategoryName ? (
            <input
              type="text"
              value={categoryName}
              onChange={(e) => {
                const newName = e.target.value;
                setCategoryName(newName);
                if (onCategoryNameChange) {
                  onCategoryNameChange(
                    attachment.attachmentCategoryId,
                    newName
                  );
                }
              }}
              className="form-control"
              style={{ marginTop: "10px", marginBottom: "10px" }}
            />
          ) : (
            attachment?.attachmentCategoryName && (
              <h6>
                <strong>{attachment?.attachmentCategoryName}</strong>
              </h6>
            )
          )}

          {!!attachment.version && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#d9edf7",
                  color: "#31708f",
                  fontWeight: "bold",
                  padding: "2px 6px",
                  fontSize: "12px",
                  borderRadius: "8px",
                  border: "1px solid #31708f",
                }}
              >
                version: {attachment.version}
              </div>
            </div>
          )}

          <Row>
            {!!attachment?.id &&
              !!attachmentMetadata?.upperFields &&
              attachmentMetadata.upperFields.length > 0 &&
              attachmentMetadata.upperFields.map((field, key) =>
                createField("upperFields", field, key)
              )}
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                {!!legend && <label>{legend}</label>}
                {!!showLegend && !legend && !!attachmentMetadata?.legend && (
                  <label>{attachmentMetadata?.legend}</label>
                )}
                <div style={{ width: "100%" }}>
                  {!!saving && <Spinner />}
                  {!saving && (
                    <CustomFileUpload
                      placeholder="+ Subir archivo"
                      name={file?.fileName}
                      value={file}
                      disabled={disabled}
                      allowReupload={!disabled}
                      onRemove={() => {
                        setShowDeleteConfirmationModal(true);
                      }}
                      onChange={async (_, file) => {
                        try {
                          setSaving(true);
                          if (file && file.fileName) {
                            const newAttachmentMetadata =
                              defaultExtendedMetadata[
                                attachment.attachmentCategoryId
                              ] || {};

                            if (attachment.attachmentCategoryId) {
                              const uploadResponse = await uploadAttachment(
                                attachmentEntityTypeId,
                                attachment.attachmentCategoryId,
                                entityId,
                                file.file,
                                newAttachmentMetadata
                              );

                              if (uploadResponse.status === 200) {
                                const newAttachment = { ...attachment };
                                newAttachment.id = uploadResponse.data;
                                newAttachment.attachmentFileName =
                                  file.fileName;
                                newAttachment.extendedMetadata = JSON.stringify(
                                  newAttachmentMetadata
                                );
                                onAttachmentChange(newAttachment);
                                toast.success(t("commons.fileUploaded"));
                              } else {
                                toast.error(
                                  t(
                                    "protocolErrors.attachment_item_delete_error"
                                  )
                                );
                                return false;
                              }
                            }
                          }
                        } catch (err) {
                          toast.error(
                            t("protocolErrors.attachment_item_upload_error")
                          );
                        } finally {
                          setSaving(false);
                        }
                      }}
                      onDownload={handlePreviewFile}
                    />
                  )}
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {!!attachment?.id &&
              !!attachmentMetadata?.lowerFields &&
              attachmentMetadata.lowerFields.length > 0 &&
              attachmentMetadata.lowerFields.map((field, key) =>
                createField("lowerFields", field, key)
              )}
          </Row>

          {/* Barra de acciones con botones */}
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              alignItems: "center",
            }}
          >
            {/* Botón Ver Historial - solo si hay archivo */}
            {!!showRestoreButton &&
              !!attachment?.id &&
              !!attachment?.attachmentCategoryId && (
                <button
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: "#f5f5f5",
                    color: "#555",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                  className="btn"
                  onClick={() => setShowHistory(true)}
                >
                  <FaHistory /> Ver Historial
                </button>
              )}
          </div>
        </div>
      )}

      <DocumentViewer
        file={response?.blob}
        fileName={response?.fileName}
        inModal={true}
        isLoading={false}
      />

      <FileHistoryModal
        show={showHistory}
        typeId={attachmentEntityTypeId}
        entityId={entityId}
        attachmentCategoryId={attachment?.attachmentCategoryId}
        onClose={() => setShowHistory(false)}
      />

      <ToastContainer position="top-right" pauseOnHover />
      {!!showDeleteConfirmationModal && (
        <ModalConfirm
          onConfirm={async () => {
            setSaving(true);
            try {
              const removeResponse = await removeAttachment(
                attachmentEntityTypeId,
                attachment.id,
                true
              );
              if (removeResponse.status === 200) {
                const newAttachment = { ...attachment };
                newAttachment.id = null;
                newAttachment.attachmentFileName = null;
                newAttachment.extendedMetadata = null;
                onAttachmentChange(newAttachment);
                setFile(undefined);
              } else {
                toast.error(t("protocolErrors.attachment_item_delete_error"));
              }
            } catch {
              toast.error(t("protocolErrors.attachment_item_delete_error"));
            } finally {
              setSaving(false);
              setShowDeleteConfirmationModal(false);
            }
          }}
          onCancel={() => setShowDeleteConfirmationModal(false)}
          title={t("protocolErrors.attachment_item_delete_title")}
          description={t("protocolErrors.attachment_item_delete_description")}
          confirmStyle="danger"
        />
      )}
    </>
  );
};

export default DocumentUploadCard;
